import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useActiveClinic } from '@/lib/activeClinic';
import { useAuth } from '@/lib/auth';
import {
  isClinicModuleEnabled,
  readActivatedModules,
} from '@/lib/clinicModules';
import {
  PERMISSION_ACTIONS,
  PERMISSION_RESOURCE_GROUPS,
  type PermissionAction,
  type PermissionMap,
  type PermissionResource,
} from '@/lib/permissions';
import { useClinic } from '@/hooks/useClinicSettings';
import {
  useClinicRoles,
  useCreateClinicRole,
  useDeleteClinicRole,
  useSetClinicRolePermissions,
  useUpdateClinicRole,
  type ClinicRoleRow,
} from '@/hooks/useClinicRoles';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Button } from '@/components/ui/Button';
import { SectionCard } from './ClinicGeneral';
import { cn } from '@/utils/cn';

/** Permission resources that only appear when their clinic module is enabled. */
const MODULE_GATED_RESOURCES: Partial<Record<PermissionResource, string>> = {
  referrals: 'referrals',
};

export function ClinicRoles() {
  const { t } = useTranslation();
  const { activeClinicId } = useActiveClinic();
  const { hasPermission } = useAuth();
  const { data: clinic } = useClinic(activeClinicId);
  const activatedModules = readActivatedModules(clinic?.activated_modules);

  const visibleResourceGroups = useMemo(
    () =>
      PERMISSION_RESOURCE_GROUPS.map((group) => ({
        ...group,
        resources: group.resources.filter((resource) => {
          const moduleKey = MODULE_GATED_RESOURCES[resource];
          if (!moduleKey) return true;
          return isClinicModuleEnabled(activatedModules, moduleKey);
        }),
      })).filter((group) => group.resources.length > 0),
    [activatedModules],
  );

  const canView = Boolean(
    activeClinicId && hasPermission(activeClinicId, 'settings_roles', 'view'),
  );
  const canEdit = Boolean(
    activeClinicId && hasPermission(activeClinicId, 'settings_roles', 'update'),
  );

  const { data: roles, isLoading, error } = useClinicRoles(canView ? activeClinicId : null);
  const createRole = useCreateClinicRole(activeClinicId);
  const updateRole = useUpdateClinicRole(activeClinicId);
  const deleteRole = useDeleteClinicRole(activeClinicId);
  const setPermissions = useSetClinicRolePermissions(activeClinicId);

  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);
  const [draft, setDraft] = useState<PermissionMap>({});
  const [newName, setNewName] = useState('');
  const [dirty, setDirty] = useState(false);

  const selected = useMemo(
    () => roles?.find((r) => r.id === selectedRoleId) ?? roles?.[0] ?? null,
    [roles, selectedRoleId],
  );

  useEffect(() => {
    if (!roles?.length) {
      setSelectedRoleId(null);
      return;
    }
    if (!selectedRoleId || !roles.some((r) => r.id === selectedRoleId)) {
      setSelectedRoleId(roles[0].id);
    }
  }, [roles, selectedRoleId]);

  useEffect(() => {
    if (!selected) {
      setDraft({});
      setDirty(false);
      return;
    }
    setDraft(structuredClone(selected.permissions));
    setDirty(false);
  }, [selected?.id, selected?.updated_at]);

  if (!activeClinicId) {
    return <ErrorAlert message={t('settings.no_clinic')} />;
  }

  if (!canView) {
    return <ErrorAlert message={t('roles.forbidden')} />;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error) return <ErrorAlert message={t('roles.load_error')} />;

  function toggle(resource: PermissionResource, action: PermissionAction) {
    if (!canEdit) return;
    setDraft((prev) => {
      const current = new Set(prev[resource] ?? []);
      if (current.has(action)) current.delete(action);
      else current.add(action);
      return { ...prev, [resource]: Array.from(current) as PermissionAction[] };
    });
    setDirty(true);
  }

  async function onSaveMatrix() {
    if (!selected || !canEdit) return;
    try {
      await setPermissions.mutateAsync({ roleId: selected.id, permissions: draft });
      setDirty(false);
      toast.success(t('roles.saved_toast'));
    } catch {
      toast.error(t('roles.save_error_toast'));
    }
  }

  async function onCreateRole() {
    const name = newName.trim();
    if (!name || !canEdit) return;
    try {
      const created = (await createRole.mutateAsync({ name })) as { id?: string } | null;
      setNewName('');
      if (created?.id) setSelectedRoleId(created.id);
      toast.success(t('roles.created_toast'));
    } catch {
      toast.error(t('roles.create_error_toast'));
    }
  }

  async function onDeleteRole(role: ClinicRoleRow) {
    if (!canEdit || role.is_system) return;
    if (!window.confirm(t('roles.delete_confirm', { name: role.name }))) return;
    try {
      await deleteRole.mutateAsync(role.id);
      toast.success(t('roles.deleted_toast'));
    } catch {
      toast.error(t('roles.delete_error_toast'));
    }
  }

  async function onRename(role: ClinicRoleRow, name: string) {
    if (!canEdit || !name.trim() || name.trim() === role.name) return;
    try {
      await updateRole.mutateAsync({ roleId: role.id, name: name.trim() });
      toast.success(t('roles.renamed_toast'));
    } catch {
      toast.error(t('roles.save_error_toast'));
    }
  }

  return (
    <div className="space-y-6">
      <SectionCard title={t('roles.title')} subtitle={t('roles.subtitle')} readOnly={!canEdit}>
        <div className="flex flex-col gap-6 lg:flex-row">
          <aside className="w-full shrink-0 space-y-3 lg:w-64">
            <ul className="space-y-1">
              {(roles ?? []).map((role) => (
                <li key={role.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedRoleId(role.id)}
                    className={cn(
                      'flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm transition-colors',
                      selected?.id === role.id
                        ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300'
                        : 'text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800',
                    )}
                  >
                    <span className="min-w-0 truncate font-medium">
                      {role.name}
                      {role.is_system ? (
                        <span className="ml-1 text-xs font-normal text-gray-500">
                          ({t('roles.system_badge')})
                        </span>
                      ) : null}
                    </span>
                    <span className="ml-2 shrink-0 text-xs text-gray-500">{role.member_count}</span>
                  </button>
                </li>
              ))}
            </ul>

            {canEdit && (
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder={t('roles.new_role_placeholder')}
                  className="min-w-0 flex-1 rounded-md border border-gray-300 px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-900"
                />
                <Button
                  type="button"
                  size="sm"
                  onClick={() => void onCreateRole()}
                  disabled={!newName.trim() || createRole.isPending}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            )}
          </aside>

          <div className="min-w-0 flex-1 space-y-4">
            {!selected ? (
              <p className="text-sm text-gray-500">{t('roles.empty')}</p>
            ) : (
              <>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    {canEdit ? (
                      <input
                        type="text"
                        defaultValue={selected.name}
                        key={selected.id}
                        onBlur={(e) => void onRename(selected, e.target.value)}
                        className="w-full max-w-sm rounded-md border border-gray-300 px-2 py-1.5 text-base font-semibold dark:border-gray-600 dark:bg-gray-900"
                        aria-label={t('roles.rename_label')}
                      />
                    ) : (
                      <h2 className="text-base font-semibold text-gray-900 dark:text-white">
                        {selected.name}
                      </h2>
                    )}
                    <p className="mt-1 text-xs text-gray-500">
                      {selected.is_system
                        ? t('roles.employee_template_hint')
                        : t('roles.custom_role_hint')}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {canEdit && !selected.is_system && (
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => void onDeleteRole(selected)}
                        disabled={deleteRole.isPending || selected.member_count > 0}
                      >
                        <Trash2 className="h-4 w-4" />
                        {t('roles.delete')}
                      </Button>
                    )}
                    {canEdit && (
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => void onSaveMatrix()}
                        disabled={!dirty || setPermissions.isPending}
                      >
                        {setPermissions.isPending ? t('settings.saving') : t('settings.save')}
                      </Button>
                    )}
                  </div>
                </div>

                <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                  <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-300">
                          {t('roles.col_resource')}
                        </th>
                        {PERMISSION_ACTIONS.map((action) => (
                          <th
                            key={action}
                            className="px-3 py-2 text-center font-medium text-gray-600 dark:text-gray-300"
                          >
                            {t(`roles.action_${action}`)}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-card-dark">
                      {visibleResourceGroups.map((group) => (
                        <Fragment key={group.groupKey}>
                          <tr className="bg-gray-50/80 dark:bg-gray-900/40">
                            <td
                              colSpan={1 + PERMISSION_ACTIONS.length}
                              className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500"
                            >
                              {t(`roles.group_${group.groupKey}`)}
                            </td>
                          </tr>
                          {group.resources.map((resource) => (
                            <tr key={resource}>
                              <td className="whitespace-nowrap px-3 py-2 text-gray-800 dark:text-gray-200">
                                {t(`roles.resource_${resource}`)}
                              </td>
                              {PERMISSION_ACTIONS.map((action) => {
                                const checked = (draft[resource] ?? []).includes(action);
                                return (
                                  <td key={action} className="px-3 py-2 text-center">
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      disabled={!canEdit}
                                      onChange={() => toggle(resource, action)}
                                      className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 disabled:opacity-50"
                                      aria-label={`${resource} ${action}`}
                                    />
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      </SectionCard>
    </div>
  );
}
